import create, { UseStore } from "zustand";
import { userProfileApi } from "../api/user-profile-api";
import { Subscription, subscription } from "./api";
import {
  AuditDetailArgs,
  AuditDetails,
  AuditReportSummary,
  auditSummaryHasResults,
  AuditWorksheet,
  getAuditDetails,
  getAuditSummary,
} from "./audit-report-api";

import {
  activeFieldCriteria,
  createDateCriteria,
  createLocalCriteria,
  DashboardSearchApi,
  dashboardSearchApi,
  dashboardSearchConfiguration,
  departmentCriteria,
  markedForDeleteCriteria,
  notWorksheetStateCriteria,
  ownerLastCriteria,
  providerIdCriteria,
  workflowActivityCriteria,
  workflowCriteria,
  workflowOwnersLastCriteria,
  worksheetStateCriteria,
  worksheetTypeCriteria,
} from "./dashboard-search-api";
import { worksheetToVisit } from "./domain";
import {
  OrganizationApi,
  OrganizationSelection,
  organizationSelectionApi,
} from "./organization-api";
import { createPageApi, PageApi, TableColumn } from "./paged-table";
import { fetchRequest } from "./";
import { showToast } from "./toast";
import { VisitSortField } from "./visit-queue";
import { currentWorkflow, workflows } from "./workflow-api";
import { worksheetMessage, WorksheetResult } from "./worksheet-service";

const service = "auditbatchquery";

// https://dev.coding-presidiohealth.com/auditbatchquery/process

export type AuditBatchQuery = {
  locationCode?: string;
  departmentCode?: string;
  pageNumber?: number;
  pageSize?: number;
  queryString?: string;
};

export type AuditBatch = {
  auditId: string;
  description: string;
  locationCode: string;
  departmentCode: string;
  worksheetTypes: string[];
  startState: string;
  action: string;
  auditAction: string;
  startDate: string;
  endDate: string;
  maximumWorksheets: number;
  userId: string;
  userWorkflow: string;
  providerId: string;
  selectionMethod: string;
  active: boolean;

  displayInList: boolean;
  currentWorksheets: number;
  auditedWorksheets: number;
  skippedWorksheets: number;
  worksheetsReturnedToCoder: number;
  ruleConfigs?: any[];
  createDate: string;
};

export type AuditBatchResult = {
  totalResults?: number;
  pageSize?: number;
  pageNumber?: number;
  searchHandle?: string;
  entries?: AuditBatch[];
};

export const getAuditBatches = fetchRequest<
  AuditBatchQuery,
  WorksheetResult<AuditBatchResult>
>({
  method: "post",
  body: (query: AuditBatchQuery) => {
    return worksheetMessage(
      { ...{ pageNumber: 0, pageSize: 20 }, ...query },
      "getauditbatches",
      service,
      currentWorkflow()
    );
  },
});

export const getAuditReview = fetchRequest<
  AuditBatchQuery,
  WorksheetResult<AuditBatchResult>
>({
  method: "post",
  body: (query: AuditBatchQuery) => {
    return worksheetMessage(
      { ...{ pageNumber: 0, pageSize: 20 }, ...query },
      "getauditreviewlist",
      service,
      currentWorkflow()
    );
  },
});

type AuditBatchData = {
  batches: AuditBatchResult;
  batch: AuditBatch;
  batchView: boolean;
  summary: AuditReportSummary;
  details: AuditDetails;
  selected: AuditWorksheet;
  drillDownTitle?: string;
};

type AuditBatchActions = {
  refreshBatchList: (org: OrganizationSelection) => void;
  startBatch: (a: AuditBatch) => void;
  clearBatch: (a?: AuditBatchQuery) => void;
  auditReview: (a?: AuditBatchQuery) => void;
  setOrg: (a: UseStore<OrganizationApi>) => AuditBatchApi;
  getSummary: (id: string) => void;
  getDetails: (id: string, category: string, value?: any) => void;
  requestPage: (a: { pageNumber: number; pageSize: number }) => void;
  requestSort: (sortOrder: number, c: TableColumn) => void;
};

export type AuditBatchApi = {
  data: UseStore<AuditBatchData>;
  actions: AuditBatchActions;
  events: Subscription<AuditBatchData>;
  org: UseStore<OrganizationApi>;
  pageApi: PageApi;
  searchApi: DashboardSearchApi;
};

function auditBatchApi(): AuditBatchApi {
  const data = create<AuditBatchData>((s) => ({
    batches: null,
    batch: null,
    batchView: false,
    summary: null,
    details: null,
    selected: null,
    drillDownTitle: null,
  }));

  const events = subscription<AuditBatchData>(["batch"], data);
  const org = organizationSelectionApi(userProfileApi);

  const searchApi = dashboardSearchApi({
    fetch: null,
    location: () => {
      const { location, department } = org.getState().selection;
      return { locationCode: location?.code, departmentCode: department.code };
    },
    detailView: "worksheets",
    defaultSearch: null,
    ...{
      configuration: dashboardSearchConfiguration({
        fields: [
          worksheetStateCriteria(),
          createDateCriteria(),
          notWorksheetStateCriteria(),
          markedForDeleteCriteria(),
          workflowCriteria(),
          ownerLastCriteria(),
          providerIdCriteria(),
          departmentCriteria(),
          worksheetTypeCriteria(),
          workflowActivityCriteria(),
          workflowOwnersLastCriteria(),
          activeFieldCriteria(),
          departmentCriteria(),
          createLocalCriteria(),
        ],
      }),
    },
  });

  async function search(
    a: AuditBatchQuery,
    fn?: (query: AuditBatchQuery) => Promise<WorksheetResult<AuditBatchResult>>
  ) {
    fn = fn || getAuditBatches;

    if (a?.locationCode) {
      const args = {
        locationCode: a.locationCode,
        departmentCode: a.departmentCode || "URGENTCARE",
        pageNumber: 0,
        pageSize: 100,
        // filters: {
        //   predicate: "AND",
        //   fields: [
        //     {
        //       fieldName: "auditId",
        //       stringFilter: "SurveillanceAuditLevels3-5"
        //     }
        //   ]
        // }
      };

      //TODO: filters ?

      const response = await fn(args);

      if (response.success) {
        data.setState({ batches: response.result });
      } else {
        const message = response.errors
          ? response.errors.join(", ")
          : "Batch request failed.";
        console.log("audit query error", message);
        showToast(message);
      }
    }
  }

  async function refreshBatchList(a: OrganizationSelection) {
    const { location, department } = { ...org.getState().selection, ...a };
    if (location) {
      search({ locationCode: location.code, departmentCode: department.code });
    }
  }

  function startBatch(batch: AuditBatch) {
    workflows.actions.setWorkflow("auditStd");
    data.setState({ batch, batchView: false });
  }

  function setOrg(a: UseStore<OrganizationApi>): AuditBatchApi {
    org.setState(a.getState());
    return api;
  }

  let lastQuery: AuditBatchQuery = null;

  function clearBatch(a?: AuditBatchQuery) {
    data.setState({
      batch: null,
      batches: null,
      summary: null,
      details: null,
      drillDownTitle: null,
    });
    if (!a) {
      a = lastQuery;
    } else {
      lastQuery = a;
    }
    search(a);
  }

  async function auditReview(a?: AuditBatchQuery) {
    search(a, getAuditReview);
  }

  async function getSummary(auditId: string) {
    data.setState({ summary: null });
    // 99203Audit
    const r = await getAuditSummary({ auditId });

    if (r.success && r.result && auditSummaryHasResults(r.result)) {
      const summary: AuditReportSummary = r.result;
      data.setState({ summary });
    } else {
      console.log("getSummary errors", r);
    }
  }

  let query: Partial<AuditDetailArgs> = {};
  function requestPage({ pageNumber, pageSize }) {
    query = { ...query, ...{ pageNumber, pageSize } };
  }

  let sort: {
    [key: string]: VisitSortField;
  } = {};

  function requestSort(order: -1, c: TableColumn) {
    let field: VisitSortField = c?.data as VisitSortField;
    if (field) {
      field.sortOrder = order;
      sort[field.fieldName] = field;
    }
    refresh();
  }

  function updateQuery(a?: Partial<AuditDetailArgs>) {
    query = { ...query, ...a };
    const fields = Object.values(sort || {});
    query.sort = fields?.length ? fields : undefined;
    return query;
  }

  function refresh() {
    request(updateQuery() as AuditDetailArgs);
  }

  async function request(a?: Partial<AuditDetailArgs>) {
    const r = await getAuditDetails(updateQuery(a) as AuditDetailArgs);

    if (r.success && r.result) {
      const list = r.result.worksheets || [];
      const worksheets = list.map((a) => worksheetToVisit(a));
      const details: AuditDetails = {
        ...r.result,
        ...{
          worksheets,
        },
      } as AuditDetails;
      data.setState({ details });
    } else {
      console.log("getDetails errors", r);
    }
  }
  async function getDetails(auditId: string, category: string, value: any[]) {
    const args = { auditId };
    args[category] = value[0];

    const drillDownTitle = value[2] || " ";
    data.setState({ drillDownTitle });

    if (category === "bubbleCategory") {
      query.changeReason = undefined;
    } else {
      query.bubbleCategory = undefined;
    }
    const a = updateQuery(args) as AuditDetailArgs;
    request(a);
  }

  const actions: AuditBatchActions = {
    refreshBatchList,
    startBatch,
    clearBatch,
    auditReview,
    setOrg,
    getSummary,
    getDetails,
    requestPage,
    requestSort,
  };

  const pageApi = createPageApi();
  const api = { data, events, actions, org, pageApi, searchApi };
  return api;
}

export const auditBatch = auditBatchApi();

export function setAuditWorksheet(a: AuditWorksheet) {
  if (a) {
    if (auditBatch) {
      auditBatch.data.setState({ selected: a });
    }
  }
}
export function auditOrganizationCode(): string {
  if (!auditBatch) {
    return null;
  }
  return auditBatch.org.getState().selection?.organization?.code;
}
